import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import { OFFICE_POSITION_MESSAGE_TYPES } from "@bezoned/desktop-bridge/office-position-messaging.js";
import type {
  OfficePositionAvatarData,
  OfficePositionAvatarUpdatedPayload,
  OfficePositionBridgeMessage,
  OfficePositionStatePayload,
} from "@bezoned/desktop-bridge/office-position-messaging.js";
import { isBridgeDebugEnabled } from "@bezoned/desktop-bridge/table-call-debug-log.js";

const DEBUG_SCOPE = "office-position-bridge-sync";

const createPositionEnvelope = (
  type: string,
  instanceId: string,
  payload: unknown
) =>
  createBridgeEnvelope({
    instanceId,
    payload,
    source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
    target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
    type,
  });

const resolveAvatarUserIds = (
  message: OfficePositionBridgeMessage
): string[] => {
  if (message.type === OFFICE_POSITION_MESSAGE_TYPES.STATE) {
    return message.payload.avatars.map((avatar) => avatar.userId);
  }

  if (message.type === OFFICE_POSITION_MESSAGE_TYPES.AVATAR_ADDED) {
    return [message.payload.avatar.userId];
  }

  if (message.type === OFFICE_POSITION_MESSAGE_TYPES.AVATAR_REMOVED) {
    return [message.payload.userId];
  }

  if (message.type === OFFICE_POSITION_MESSAGE_TYPES.AVATAR_UPDATED) {
    return [message.payload.userId];
  }

  return [];
};

const logPositionBridgeMessage = ({
  room,
  message,
}: {
  room: string;
  message: OfficePositionBridgeMessage;
}): void => {
  if (!isBridgeDebugEnabled(import.meta.env.DEV)) {
    return;
  }

  const avatarUserIds = resolveAvatarUserIds(message);

  console.debug(`[${DEBUG_SCOPE}]`, {
    avatarCount: avatarUserIds.length,
    avatarUserIds,
    direction: "outbound",
    messageId: message.messageId,
    room,
    sentAt: message.sentAt,
    timestamp: Date.now(),
    type: message.type,
  });
};

export const createPositionDeltaSenders = ({
  instanceId,
  bridgeRoom,
  sendJson,
}: {
  instanceId: string | null;
  bridgeRoom: string;
  sendJson: (room: string, data: unknown) => void;
}) => {
  const sendDelta = (type: string, payload: unknown) => {
    if (!instanceId) {
      return;
    }

    const message = createPositionEnvelope(
      type,
      instanceId,
      payload
    ) as OfficePositionBridgeMessage;
    logPositionBridgeMessage({ message, room: bridgeRoom });
    sendJson(bridgeRoom, message);
  };

  const sendPositionState = (payload: OfficePositionStatePayload) => {
    sendDelta(OFFICE_POSITION_MESSAGE_TYPES.STATE, payload);
  };

  const sendAvatarAdded = (avatar: OfficePositionAvatarData) => {
    sendDelta(OFFICE_POSITION_MESSAGE_TYPES.AVATAR_ADDED, { avatar });
  };

  const sendAvatarRemoved = (userId: string) => {
    sendDelta(OFFICE_POSITION_MESSAGE_TYPES.AVATAR_REMOVED, { userId });
  };

  const sendAvatarUpdated = (payload: OfficePositionAvatarUpdatedPayload) => {
    sendDelta(OFFICE_POSITION_MESSAGE_TYPES.AVATAR_UPDATED, payload);
  };

  return {
    sendAvatarAdded,
    sendAvatarRemoved,
    sendAvatarUpdated,
    sendPositionState,
  };
};
